import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, Save, X, FileDown, BellRing, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { SummaryCards, CalendarGrid } from "../../components/employee/TimesheetCalendar";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli",
  "August", "September", "Oktober", "November", "Dezember"];
const empty = { name: "", email: "", password: "", hourly_rate: 0, monthly_limit_hours: 0, active: true,
  personnel_number: "", employment_type: "Minijobber", work_days_per_week: "", daily_hours: "",
  work_schedule: "flexibel", vacation_days_year: "" };

export default function Employees() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [viewing, setViewing] = useState(null); // employee for timesheet view

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/employees");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing.name?.trim() || !editing.email?.trim()) return toast.error("Name und E-Mail sind Pflicht.");
    if (!editing.id && !editing.password) return toast.error("Bitte ein Passwort für den neuen Mitarbeiter vergeben.");
    setSaving(true);
    try {
      const num = (v) => v === "" || v === null || v === undefined ? null : Number(v);
      const payload = {
        name: editing.name, email: editing.email,
        hourly_rate: Number(editing.hourly_rate) || 0,
        monthly_limit_hours: Number(editing.monthly_limit_hours) || 0,
        active: editing.active,
        personnel_number: editing.personnel_number || null,
        employment_type: editing.employment_type || null,
        work_days_per_week: num(editing.work_days_per_week),
        daily_hours: num(editing.daily_hours),
        work_schedule: editing.work_schedule || "flexibel",
        vacation_days_year: num(editing.vacation_days_year),
      };
      if (editing.password) payload.password = editing.password;
      if (editing.id) await api.patch(`/admin/employees/${editing.id}`, payload);
      else await api.post("/admin/employees", payload);
      toast.success("Mitarbeiter gespeichert.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Mitarbeiter und alle Zeiteinträge löschen?")) return;
    await api.delete(`/admin/employees/${id}`);
    toast.success("Gelöscht.");
    load();
  };

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/reminders/send");
      toast.success(`Erinnerungen versendet: ${data.sent}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-sm border border-light-gray bg-light-gray px-3 py-2.5 text-sm outline-none focus:border-yellow";

  return (
    <div data-testid="admin-employees">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-anthracite">Mitarbeiter &amp; Stunden</h1>
          <p className="mt-1 text-mid-gray">Logins anlegen, Stundensatz &amp; monatliches Limit an Arbeitsstunden festlegen, Nachweise als PDF.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={sendReminders} disabled={sending} className="inline-flex items-center gap-2 rounded-sm border border-light-gray bg-white px-4 py-2.5 font-heading text-sm font-bold text-anthracite hover:border-yellow disabled:opacity-60" data-testid="send-reminders">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BellRing className="h-4 w-4" />} Erinnerungen senden
          </button>
          <button onClick={() => setEditing({ ...empty })} className="inline-flex items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black hover:bg-yellow-dark" data-testid="employee-new">
            <Plus className="h-4 w-4" /> Neuer Mitarbeiter
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>
      ) : items.length === 0 ? (
        <p className="mt-10 rounded-md border border-dashed border-mid-gray/30 bg-white p-12 text-center text-mid-gray" data-testid="employees-empty">Noch keine Mitarbeiter angelegt.</p>
      ) : (
        <div className="mt-6 overflow-x-auto rounded-md border border-light-gray bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-light-gray bg-light-gray text-left text-xs uppercase tracking-wider text-mid-gray">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Personalnr.</th>
                <th className="px-4 py-3">E-Mail</th>
                <th className="px-4 py-3">Stundensatz</th>
                <th className="px-4 py-3">Monatliches Limit</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-light-gray/60 hover:bg-light-gray/40" data-testid={`employee-row-${e.id}`}>
                  <td className="px-4 py-3 font-semibold text-anthracite">{e.name}</td>
                  <td className="px-4 py-3 text-mid-gray">{e.personnel_number || "—"}</td>
                  <td className="px-4 py-3 text-mid-gray">{e.email}</td>
                  <td className="px-4 py-3 text-mid-gray">{Number(e.hourly_rate).toFixed(2)} €</td>
                  <td className="px-4 py-3 text-mid-gray">{e.monthly_limit_hours > 0 ? `${e.monthly_limit_hours} h` : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-sm px-2 py-1 text-xs font-semibold ${e.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{e.active ? "Aktiv" : "Inaktiv"}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => setViewing(e)} className="inline-flex items-center gap-1 text-xs font-bold text-anthracite hover:text-yellow-dark" data-testid={`employee-timesheet-${e.id}`}><CalendarDays className="h-4 w-4" /> Stunden</button>
                      <button onClick={() => setEditing({ ...e, password: "" })} className="text-xs font-bold text-anthracite hover:text-yellow-dark" data-testid={`employee-edit-${e.id}`}>Bearbeiten</button>
                      <button onClick={() => del(e.id)} className="text-red-500" data-testid={`employee-del-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-black/60 p-4" data-testid="employee-editor">
          <div className="my-8 w-full max-w-lg rounded-md bg-white p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl font-bold text-anthracite">{editing.id ? "Mitarbeiter bearbeiten" : "Neuer Mitarbeiter"}</h2>
              <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-mid-gray" /></button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">Name *<input value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} className={inputCls} data-testid="employee-field-name" /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">E-Mail (Login) *<input type="email" value={editing.email} onChange={(e) => setEditing((p) => ({ ...p, email: e.target.value }))} className={inputCls} data-testid="employee-field-email" /></label>
              <label className="text-sm font-semibold text-anthracite sm:col-span-2">{editing.id ? "Neues Passwort (leer = unverändert)" : "Passwort *"}<input type="text" value={editing.password || ""} onChange={(e) => setEditing((p) => ({ ...p, password: e.target.value }))} className={inputCls} data-testid="employee-field-password" autoComplete="new-password" /></label>
              <label className="text-sm font-semibold text-anthracite">Stundensatz (€)<input type="number" step="0.01" min="0" value={editing.hourly_rate} onChange={(e) => setEditing((p) => ({ ...p, hourly_rate: e.target.value }))} className={inputCls} data-testid="employee-field-rate" /></label>
              <label className="text-sm font-semibold text-anthracite">Monatliches Limit an Arbeitsstunden (h)<input type="number" step="0.5" min="0" value={editing.monthly_limit_hours} onChange={(e) => setEditing((p) => ({ ...p, monthly_limit_hours: e.target.value }))} className={inputCls} data-testid="employee-field-limit" /></label>
              <label className="text-sm font-semibold text-anthracite">Personalnummer<input value={editing.personnel_number || ""} onChange={(e) => setEditing((p) => ({ ...p, personnel_number: e.target.value }))} className={inputCls} data-testid="employee-field-personnelnr" /></label>
              <label className="text-sm font-semibold text-anthracite">Beschäftigungsart<input value={editing.employment_type || ""} placeholder="z. B. Minijobber" onChange={(e) => setEditing((p) => ({ ...p, employment_type: e.target.value }))} className={inputCls} data-testid="employee-field-emptype" /></label>
              <label className="text-sm font-semibold text-anthracite">Regelmäßige Arbeitstage/Woche<input type="number" step="1" min="0" max="7" value={editing.work_days_per_week ?? ""} onChange={(e) => setEditing((p) => ({ ...p, work_days_per_week: e.target.value }))} className={inputCls} data-testid="employee-field-workdays" /></label>
              <label className="text-sm font-semibold text-anthracite">Sollzeit je Arbeitstag (h, z. B. 6.5)<input type="number" step="0.25" min="0" max="24" value={editing.daily_hours ?? ""} onChange={(e) => setEditing((p) => ({ ...p, daily_hours: e.target.value }))} className={inputCls} data-testid="employee-field-dailyhours" /></label>
              <label className="text-sm font-semibold text-anthracite">Arbeitszeitmodell
                <select value={editing.work_schedule || "flexibel"} onChange={(e) => setEditing((p) => ({ ...p, work_schedule: e.target.value }))} className={inputCls} data-testid="employee-field-schedule">
                  <option value="flexibel">flexibel</option>
                  <option value="fest">fest</option>
                </select>
              </label>
              <label className="text-sm font-semibold text-anthracite">Jahresurlaubsanspruch (Tage)<input type="number" step="0.5" min="0" max="60" value={editing.vacation_days_year ?? ""} onChange={(e) => setEditing((p) => ({ ...p, vacation_days_year: e.target.value }))} className={inputCls} data-testid="employee-field-vacdays" /></label>
              <p className="text-xs text-mid-gray sm:col-span-2">
                Berechnungshilfe: Regelmäßige Arbeitstage × 30 ÷ 5 = Jahresurlaub. Der Wert ist bearbeitbar,
                wenn vertraglich abweichende Regelungen bestehen.
              </p>
              <label className="flex items-center gap-2 text-sm text-anthracite sm:col-span-2"><input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing((p) => ({ ...p, active: e.target.checked }))} className="accent-yellow" data-testid="employee-field-active" /> Konto aktiv (Login möglich)</label>
            </div>
            <button onClick={save} disabled={saving} className="mt-5 inline-flex items-center gap-2 rounded-sm bg-yellow px-5 py-3 font-heading font-bold text-brand-black hover:bg-yellow-dark disabled:opacity-60" data-testid="employee-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
            </button>
          </div>
        </div>
      )}

      {viewing && <TimesheetView employee={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function TimesheetView({ employee, onClose }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayOpen, setDayOpen] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/employees/${employee.id}/timesheet`, { params: { year, month } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, [employee.id, year, month]);
  useEffect(() => { load(); }, [load]);

  const entriesByDate = {};
  (data?.entries || []).forEach((e) => { (entriesByDate[e.date] = entriesByDate[e.date] || []).push(e); });

  const prev = () => { if (month === 1) { setMonth(12); setYear((y) => y - 1); } else setMonth((m) => m - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear((y) => y + 1); } else setMonth((m) => m + 1); };

  const openDay = (dateStr) => { setDayOpen(dateStr); setForm(null); };
  const startNew = () => setForm({ start: "08:00", end: "16:00", break_minutes: 30, note: "", kind: "work" });

  const saveEntry = async () => {
    if (!form?.start || !form?.end) return toast.error("Bitte Beginn und Ende angeben.");
    setSaving(true);
    try {
      const payload = { date: dayOpen, start: form.start, end: form.end,
        break_minutes: Number(form.break_minutes) || 0, note: form.note || "",
        kind: form.kind || "work" };
      if (form.id) await api.patch(`/admin/time-entries/${form.id}`, payload);
      else await api.post(`/admin/employees/${employee.id}/time-entries`, payload);
      toast.success("Eintrag gespeichert.");
      setForm(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const delEntry = async (id) => {
    if (!window.confirm("Diesen Arbeitszeiteintrag wirklich löschen?")) return;
    try {
      await api.delete(`/admin/time-entries/${id}`);
      toast.success("Eintrag gelöscht.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadPdf = async () => {
    try {
      // Cache-Buster: erzwingt bei jedem Klick einen frischen Server-Request,
      // damit gerade eben geänderte oder gelöschte Arbeitszeiten sofort im PDF erscheinen.
      const res = await api.get(`/admin/employees/${employee.id}/timesheet.pdf`, {
        params: { year, month, _ts: Date.now() }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `arbeitszeitnachweis-${(employee.name || "mitarbeiter").replace(/\s+/g, "_")}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF konnte nicht erstellt werden.");
    }
  };

  const dfmt = (ds) => { const [y, m, d] = ds.split("-"); return `${d}.${m}.${y}`; };
  const dayEntries = dayOpen ? (entriesByDate[dayOpen] || []) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-brand-black/60 p-4" data-testid="employee-timesheet-view" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="my-8 w-full max-w-3xl rounded-md bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-heading text-xl font-bold text-anthracite">Stundennachweis · {employee.name}</h2>
          <div className="flex items-center gap-2">
            <button onClick={downloadPdf} className="inline-flex items-center gap-2 rounded-sm bg-anthracite px-3 py-2 font-heading text-sm font-bold text-white hover:bg-brand-black" data-testid="ts-pdf">
              <FileDown className="h-4 w-4" /> PDF
            </button>
            <button onClick={onClose} data-testid="ts-view-close"><X className="h-5 w-5 text-mid-gray" /></button>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-sm border border-light-gray bg-white px-2 py-1.5 w-fit">
          <button onClick={prev} className="rounded-sm p-1.5 hover:bg-light-gray" aria-label="Vorheriger Monat"><ChevronLeft className="h-4 w-4" /></button>
          <span className="min-w-[140px] text-center font-heading text-sm font-bold text-anthracite" data-testid="tsv-month">{MONTHS[month - 1]} {year}</span>
          <button onClick={next} className="rounded-sm p-1.5 hover:bg-light-gray" aria-label="Nächster Monat"><ChevronRight className="h-4 w-4" /></button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-yellow" /></div>
        ) : (
          <>
            <div className="mt-4"><SummaryCards s={data} /></div>
            <CalendarGrid year={year} month={month} entriesByDate={entriesByDate} onDayClick={openDay} />
            <p className="mt-2 text-xs text-mid-gray">Klicken Sie auf einen Tag, um Arbeitszeiten anzulegen oder zu bearbeiten.</p>
          </>
        )}

        {dayOpen && (
          <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-brand-black/70 p-4" onClick={(e) => { if (e.target === e.currentTarget) { setDayOpen(null); setForm(null); } }} data-testid="admin-day-editor">
            <div className="my-8 w-full max-w-lg rounded-md bg-white p-6">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-anthracite">Tagesbearbeitung · {dfmt(dayOpen)}</h3>
                <button onClick={() => { setDayOpen(null); setForm(null); }} aria-label="Schließen"><X className="h-5 w-5 text-mid-gray" /></button>
              </div>
              <div className="mt-4 space-y-2">
                {dayEntries.length === 0 && !form && (
                  <p className="rounded-sm border border-dashed border-light-gray p-3 text-sm text-mid-gray">Keine Einträge an diesem Tag.</p>
                )}
                {dayEntries.map((e) => {
                  const kind = e.kind || "work";
                  const label = kind === "vacation" ? "Urlaub" : kind === "sick" ? "Krankheit" : `${e.start}–${e.end}`;
                  return (
                    <div key={e.id} className="flex items-center justify-between rounded-sm border border-light-gray p-3" data-testid={`admin-day-entry-${e.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-anthracite">{label} · {(e.hours || 0).toFixed(2)} h</p>
                        <p className="text-xs text-mid-gray">
                          {kind === "work" ? `Pause ${e.break_minutes} min` : (kind === "vacation" ? "Urlaubstag" : "Krankheitstag")}
                          {e.note ? ` · ${e.note}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setForm({ id: e.id, start: e.start, end: e.end, break_minutes: e.break_minutes, note: e.note || "", kind })} className="text-xs font-bold text-anthracite hover:text-yellow-dark" data-testid={`admin-day-edit-${e.id}`}>Bearbeiten</button>
                        <button onClick={() => delEntry(e.id)} className="text-red-500" data-testid={`admin-day-del-${e.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {form ? (
                <div className="mt-4 space-y-3 rounded-sm bg-light-gray p-4" data-testid="admin-day-form">
                  <label className="block text-sm font-semibold text-anthracite">Eintragsart
                    <select value={form.kind || "work"} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="admin-entry-kind">
                      <option value="work">Arbeitszeit</option>
                      <option value="vacation">Urlaub</option>
                      <option value="sick">Krankheit</option>
                    </select>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="text-sm font-semibold text-anthracite">Von<input type="time" value={form.start} onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="admin-entry-start" /></label>
                    <label className="text-sm font-semibold text-anthracite">Bis<input type="time" value={form.end} onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="admin-entry-end" /></label>
                  </div>
                  <label className="block text-sm font-semibold text-anthracite">Pause (Minuten)<input type="number" min="0" value={form.break_minutes} onChange={(e) => setForm((f) => ({ ...f, break_minutes: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="admin-entry-break" /></label>
                  <label className="block text-sm font-semibold text-anthracite">Bemerkung / Tätigkeit<input value={form.note || ""} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} className="mt-1 w-full rounded-sm border border-light-gray bg-white px-3 py-2 text-sm outline-none focus:border-yellow" data-testid="admin-entry-note" /></label>
                  <div className="flex gap-2">
                    <button onClick={saveEntry} disabled={saving} className="inline-flex items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black hover:bg-yellow-dark disabled:opacity-60" data-testid="admin-entry-save">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
                    </button>
                    <button onClick={() => setForm(null)} className="rounded-sm border border-light-gray px-4 py-2.5 text-sm font-semibold text-mid-gray hover:bg-white">Abbrechen</button>
                  </div>
                </div>
              ) : (
                <button onClick={startNew} className="mt-4 inline-flex items-center gap-2 rounded-sm bg-yellow px-4 py-2.5 font-heading text-sm font-bold text-brand-black hover:bg-yellow-dark" data-testid="admin-entry-new">
                  <Plus className="h-4 w-4" /> Neuer Eintrag
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
